import type { SourcePostFilters } from '@/hooks/useSourcePosts';
import { normalizujMiejsca, parseArea, serializeArea } from '@/lib/areaSearch';
import { uporzadkujObszary } from '@/lib/areaSelection';
import { TYPY_BUDYNKU, UDOGODNIENIA, WIEKI_BUDYNKU, type TypBudynku, type Udogodnienie, type WiekBudynku } from '@/lib/filtryMiekkie';
import { RODZAJE_BEZ_AGENCJI } from '@/lib/bezAgencji';

/**
 * SERP filters ↔ URL query params (extracted from SearchPage so the
 * round-trip is unit-testable without a router). Only non-default values are
 * emitted — keeps URLs short for the common no-filter case; browser-back from
 * an offer page restores the exact filtered view.
 */

/**
 * Wartości filtra „dodane w ciągu” (godziny; 720 = 30 dni, 2160 = 90 dni). Panel filtrów
 * pokazuje je jako opcje, a każda ma etykietę `search.filters.posted_options.<wartość>`.
 * Adres przyjmuje TYLKO je: dowolna liczba (`?postedSinceHours=48`) dawała na pasku filtrów
 * surowy klucz `search.filters.posted_options.48`.
 */
export const POSTED_OPTIONS = ['0', '24', '72', '168', '720', '2160'];

export const DEFAULT_FILTERS: SourcePostFilters = {
  // Stan filtrów to odzwierciedla, ale o wycięciu agencji decyduje zapytanie (src/lib/bezAgencji.ts).
  postingTypes: [...RODZAJE_BEZ_AGENCJI],
  postedSinceHours: null,
};

/**
 * „WYCZYŚĆ" ZDEJMUJE WSZYSTKO, LOKALIZACJĘ TEŻ (Maciek 14.10, plan §1.69; zmienia zasadę Piotra
 * z 28.09). × w rzędzie filtrów, przycisk „Wyczyść filtry" i „Wyczyść" w stopce panelu wracają
 * do całej Warszawy: obszary, dzielnice, osiedla, stacje, miejsce „Blisko do…" (wszystko to
 * siedzi w `areas`) i narysowany kształt idą razem z resztą. Zasada „lokalizacja zostaje"
 * powstała, gdy lokalizacja nie była filtrem; w zapisanych filtrach jest. Zostaje widok
 * (lista/mapa), bo to nie filtr, i miasto, bo nie jest w filtrach. Pomyłkę cofa „Cofnij"
 * w komunikacie „Wyczyszczono N filtrów" — przywraca cały poprzedni stan.
 */
/**
 * Jeden ruch „Wyczyść": stan po wyczyszczeniu, N do komunikatu i stan, który przywraca „Cofnij".
 * `liczbaFiltrow` to filtry poza lokalizacją (chipy strony); lokalizację dolicza się tutaj.
 * `ile === 0` znaczy „nie ma czego czyścić" — strona nie pokazuje wtedy komunikatu.
 */
export function wyczyscZCofnieciem(
  f: SourcePostFilters,
  liczbaFiltrow: number,
): { czyste: SourcePostFilters; ile: number; cofnij: SourcePostFilters } {
  return { czyste: wyczyszczoneFiltry(f), ile: liczbaFiltrow + liczbaElementowLokalizacji(f), cofnij: f };
}

/** Sama lokalizacja z filtrów — do „Cofnij" po zdjęciu jej ×. Stacje metra są w `areas`. */
export type Lokalizacja = Pick<SourcePostFilters, 'areas' | 'suburbs' | 'polygon'>;

export function lokalizacjaZ(f: SourcePostFilters): Lokalizacja {
  return { areas: f.areas, suburbs: f.suburbs, polygon: f.polygon };
}

/** Filtry bez lokalizacji — reszta nietknięta (× „wróć do całego miasta"). */
export function bezLokalizacji(f: SourcePostFilters): SourcePostFilters {
  return { ...f, areas: undefined, suburbs: undefined, polygon: undefined };
}

/**
 * „Cofnij" po ×: przywraca lokalizację DOKŁADNIE taką, jaka była, na BIEŻĄCYCH filtrach —
 * jeśli ktoś zdążył w międzyczasie zmienić cenę, ta zmiana zostaje.
 */
export function zLokalizacja(f: SourcePostFilters, lok: Lokalizacja): SourcePostFilters {
  return { ...f, areas: lok.areas, suburbs: lok.suburbs, polygon: lok.polygon };
}

export function wyczyszczoneFiltry(f: SourcePostFilters): SourcePostFilters {
  return { ...DEFAULT_FILTERS, ...(f.view ? { view: f.view } : {}) };
}

/**
 * Ile elementów lokalizacji zdejmie „Wyczyść" — każdy obszar (dzielnica, osiedle, stacja,
 * miejsce) i każda dzielnica z `suburbs` liczy się jak jeden filtr, narysowany kształt też.
 * Doliczane do liczby w komunikacie „Wyczyszczono N filtrów".
 */
export function liczbaElementowLokalizacji(f: SourcePostFilters): number {
  return (f.areas?.length ?? 0) + (f.suburbs?.length ?? 0) + (f.polygon && f.polygon.length >= 3 ? 1 : 0);
}

/**
 * WIDOK MAPY W ADRESIE: kamera i otwarta oferta.
 *
 * To NIE są filtry i nie mogą nimi być — filtry wchodzą do klucza zapytań, więc każde
 * przesunięcie mapy odpalałoby nowe pobranie. Jadą za to w tym samym adresie, i to jest
 * decyzja, nie skrót:
 *
 *  • „wstecz" z oferty wraca do TEGO SAMEGO kadru za darmo — przeglądarka przywraca adres,
 *    a stan mapy jest w adresie (bez tego wracał kadr domyślny na całą Warszawę);
 *  • odświeżenie strony też go zachowuje, czego `sessionStorage` nie gwarantuje;
 *  • link daje się wysłać drugiej osobie i pokazuje jej to samo, co widziałem — tak działa
 *    Zillow i Airbnb.
 *
 * ZAOKRĄGLENIE jest częścią kontraktu: 5 miejsc po przecinku to ~1 m (dokładniej niż widzi
 * oko przy każdym przybliżeniu), zoom do 0,01 (od 29.09; przy 0,1 odtworzony kadr rozjeżdżał się
 * o 5–12 px na brzegach mapy — kontrola powrotu z oferty). Bez tego adres zmieniałby się przy każdym
 * pikselu ruchu i puchł do nieczytelności.
 */
export interface WidokMapy {
  kamera?: { lat: number; lng: number; zoom: number } | null;
  /** Oferta otwarta z mapy — karuzela (telefon) albo podświetlona karta (pulpit). */
  oferta?: string | null;
}

/** `lat,lng,zoom` — jeden parametr zamiast trzech, bo zawsze zmieniają się razem. */
export function kameraDoParametru(k: { lat: number; lng: number; zoom: number }): string {
  return `${k.lat.toFixed(5)},${k.lng.toFixed(5)},${k.zoom.toFixed(2)}`;
}

/**
 * Odwrotność, z sprawdzeniem zakresów. Adres przychodzi od człowieka i od innych stron,
 * więc „52,21" i „abc" muszą dać `null`, a nie mapę nad Morzem Norweskim.
 */
export function parametrDoKamery(v: string | null): WidokMapy['kamera'] {
  if (!v) return null;
  const [lat, lng, zoom] = v.split(',').map(Number);
  if (![lat, lng, zoom].every(Number.isFinite)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  if (zoom < 0 || zoom > 24) return null;
  return { lat, lng, zoom };
}

export function paramsToWidokMapy(params: URLSearchParams): WidokMapy {
  return {
    kamera: parametrDoKamery(params.get('m')),
    oferta: params.get('oferta') || null,
  };
}

/**
 * `mapaNaEkranie` — czy mapa jest w tej chwili widoczna. Na telefonie to `view=map`; na pulpicie
 * mapa stoi obok listy ZAWSZE, choć `view` zostaje „list” — bez tej informacji kadr pulpitu nigdy
 * nie trafiał do adresu i powrót z oferty robił pierwsze dopasowanie do dzielnic (zgłoszenie
 * Piotra 29.09). Domyślnie `view === 'map'` — tak jak dotąd.
 */
export function filtersToParams(
  f: SourcePostFilters,
  widok?: WidokMapy,
  opcje: { mapaNaEkranie?: boolean } = {},
): URLSearchParams {
  const mapaNaEkranie = opcje.mapaNaEkranie ?? f.view === 'map';
  const params = new URLSearchParams();
  if (f.areas?.length) params.set('areas', f.areas.map(serializeArea).join('|'));
  if (f.suburbs?.length) params.set('suburbs', f.suburbs.join(','));
  if (typeof f.rentMin === 'number') params.set('rentMin', String(f.rentMin));
  if (typeof f.rentMax === 'number') params.set('rentMax', String(f.rentMax));
  if (f.costMode === 'total') params.set('cost', 'total');
  if (f.rooms?.length) params.set('rooms', [...f.rooms].sort().join(','));
  if (typeof f.areaMin === 'number') params.set('areaMin', String(f.areaMin));
  if (typeof f.areaMax === 'number') params.set('areaMax', String(f.areaMax));
  if (f.nearMetro) params.set('metro', '1');
  if (f.poi?.length) params.set('poi', [...f.poi].sort().join(','));
  if (typeof f.postedSinceHours === 'number') params.set('postedSinceHours', String(f.postedSinceHours));
  // `includeAgency` NIE JEST JUŻ WYPISYWANY: przełącznik „pokaż też agencje" zniknął z
  // filtrów (decyzja Piotra 27.09), więc adres nie ma jak opisać stanu, którego nie da
  // się ustawić. Model filtra zostaje — zmienia się to, co potrafi ustawić człowiek.
  // `platforms` i `fbOnly` NIE SĄ JUŻ W ADRESIE (DEC-04, 28.09 — filtr platform usunięty z
  // publicznej wyszukiwarki). Stare linki z tymi parametrami otwierają się bez nich.
  if (f.includeDead) params.set('includeDead', '1');
  if (f.noPrice) params.set('noPrice', '1');
  if (f.approxLocation) params.set('approxLoc', '1');
  if (f.sanityOutliers) params.set('sanityOutliers', '1');
  // `pets=1` niesie od 28.09 JEDYNY filtr zwierząt (miękki); stare linki z tym parametrem
  // otwierają się więc z nowym znaczeniem — tak, jak zdecydował Piotr.
  if (f.zeZwierzeciem) params.set('pets', '1');
  if (f.nieznaneOplaty) params.set('nieznane', '1');
  // „Pokaż oferty bez zdjęć” — w adresie tylko stan niedomyślny (wyłączony).
  if (f.bezZdjec === false) params.set('bezZdjec', '0');
  if (f.bezAdresu) params.set('bezAdresu', '1');
  // Filtry miękkie etapu 1b (src/lib/filtryMiekkie.ts) — krótkie, angielskie wartości w adresie.
  if (f.umeblowanie) params.set('furnished', f.umeblowanie === 'umeblowane' ? 'yes' : 'no');
  if (f.pietro === 'parter') params.set('floor', 'ground');
  if (f.kuchnia) params.set('kitchen', f.kuchnia === 'oddzielna' ? 'separate' : 'annex');
  if (f.lazienki) params.set('baths', f.lazienki === '1' ? '1' : '2plus');
  if (f.balkon) params.set('balcony', '1');
  if (f.winda) params.set('elevator', '1');
  if (f.kaucjaMax != null) params.set('depositMax', String(Math.round(f.kaucjaMax)));
  if (f.udogodnienia?.length) params.set('amenities', [...f.udogodnienia].sort().join(','));
  if (f.internet === 'swiatlowod') params.set('internet', 'fibre');
  if (f.garaz) params.set('garage', '1');
  // Budynek (A3): wartość słownika ogłoszeń wprost — jest już krótka i angielska.
  if (f.typBudynku) params.set('building', f.typBudynku);
  if (f.wiekBudynku) params.set('buildingAge', f.wiekBudynku);
  if (f.availableNow) params.set('odzaraz', '1');
  if (f.wlascicielNaFlatHero) params.set('wlasciciel', '1');
  // Sam przełącznik, nigdy lista — identyfikatory dokłada strona z ulubionych oglądającego.
  if (f.ulubione) params.set('ulubione', '1');
  if (f.sort && f.sort !== 'newest') params.set('sort', f.sort);
  if (f.view === 'map') params.set('view', 'map');
  // Draw-your-own-area polygon: `lat,lon;lat,lon;…` (5 dp keeps the URL short).
  if (f.polygon && f.polygon.length >= 3) {
    params.set('poly', f.polygon.map(([lat, lon]) => `${lat.toFixed(5)},${lon.toFixed(5)}`).join(';'));
  }
  // Widok mapy na końcu i tylko wtedy, gdy mapa jest na ekranie: w samej liście (telefon)
  // kamera nie ma czego opisywać, a zostawiona w adresie wracałaby przy następnym przełączeniu
  // jako kadr sprzed godziny.
  if (mapaNaEkranie && widok?.kamera) params.set('m', kameraDoParametru(widok.kamera));
  if (f.view === 'map' && widok?.oferta) params.set('oferta', widok.oferta);
  return params;
}

export function paramsToFilters(params: URLSearchParams): SourcePostFilters {
  const filters: SourcePostFilters = { ...DEFAULT_FILTERS };
  const areas = params.get('areas');
  if (areas) {
    const parsed = areas.split('|').map(parseArea).filter((a): a is NonNullable<typeof a> => !!a);
    // Reguły zawierania także przy wczytaniu: link sprzed 19.10 mógł nieść „Rembertów + Nowy Rembertów”.
    const areasOk = uporzadkujObszary(normalizujMiejsca(parsed));
    if (areasOk.length) filters.areas = areasOk;
  }
  const suburbs = params.get('suburbs');
  // Legacy links (?suburbs=Wola,Ochota) become district area chips so the new
  // bar shows and edits them; the param round-trips as areas= from then on.
  if (suburbs && !filters.areas) {
    const names = suburbs.split(',').filter(Boolean);
    if (names.length) filters.areas = names.map((name) => ({ kind: 'district' as const, name }));
  }
  const rentMin = params.get('rentMin');
  if (rentMin && !isNaN(Number(rentMin))) filters.rentMin = Number(rentMin);
  const rentMax = params.get('rentMax');
  if (rentMax && !isNaN(Number(rentMax))) filters.rentMax = Number(rentMax);
  if (params.get('cost') === 'total') filters.costMode = 'total';
  const rooms = params.get('rooms');
  if (rooms) {
    const parsed = rooms
      .split(',')
      .map(Number)
      .filter((n) => Number.isInteger(n) && n >= 1 && n <= 4);
    if (parsed.length) filters.rooms = parsed;
  }
  const areaMin = params.get('areaMin');
  if (areaMin && !isNaN(Number(areaMin))) filters.areaMin = Number(areaMin);
  const areaMax = params.get('areaMax');
  if (areaMax && !isNaN(Number(areaMax))) filters.areaMax = Number(areaMax);
  if (params.get('metro') === '1') filters.nearMetro = true;
  const poi = params.get('poi');
  if (poi) {
    const parsed = poi
      .split(',')
      .filter((p): p is 'transit' | 'walk' | 'green' => p === 'transit' || p === 'walk' || p === 'green');
    if (parsed.length) filters.poi = parsed;
  }
  const postedSinceHours = params.get('postedSinceHours');
  if (postedSinceHours && POSTED_OPTIONS.includes(String(Number(postedSinceHours)))) {
    filters.postedSinceHours = Number(postedSinceHours);
  }
  // STARE ADRESY Z `includeAgency=1` MAJĄ DZIAŁAĆ, tylko bez agencji: parametr jest
  // świadomie ignorowany, a nie „nieznany". Wywracanie strony albo cichy powrót agencji
  // to dwa sposoby na to samo — pokazanie czegoś, czego dziś ustawić się nie da.
  if (params.get('pets') === '1') filters.zeZwierzeciem = true;
  const meble = params.get('furnished');
  if (meble === 'yes') filters.umeblowanie = 'umeblowane';
  if (meble === 'no') filters.umeblowanie = 'nieumeblowane';
  if (params.get('floor') === 'ground') filters.pietro = 'parter';
  const kuchnia = params.get('kitchen');
  if (kuchnia === 'separate') filters.kuchnia = 'oddzielna';
  if (kuchnia === 'annex') filters.kuchnia = 'aneks';
  const lazienki = params.get('baths');
  if (lazienki === '1') filters.lazienki = '1';
  if (lazienki === '2plus') filters.lazienki = '2+';
  if (params.get('balcony') === '1') filters.balkon = true;
  if (params.get('elevator') === '1') filters.winda = true;
  if (params.get('internet') === 'fibre') filters.internet = 'swiatlowod';
  if (params.get('garage') === '1') filters.garaz = true;
  const typBudynku = params.get('building');
  if (typBudynku && (TYPY_BUDYNKU as readonly string[]).includes(typBudynku)) filters.typBudynku = typBudynku as TypBudynku;
  const wiekBudynku = params.get('buildingAge');
  if (wiekBudynku && (WIEKI_BUDYNKU as readonly string[]).includes(wiekBudynku)) filters.wiekBudynku = wiekBudynku as WiekBudynku;
  // Tylko nieujemna liczba całkowita; śmieci w adresie nie tworzą filtra.
  const kaucja = params.get('depositMax');
  if (kaucja != null && /^\d{1,6}$/.test(kaucja)) filters.kaucjaMax = Number(kaucja);
  // Tylko znane klucze; nieznane (stary link, literówka) po cichu odpadają.
  const udog = (params.get('amenities') ?? '').split(',').filter((u): u is Udogodnienie => (UDOGODNIENIA as readonly string[]).includes(u));
  if (udog.length) filters.udogodnienia = [...new Set(udog)];
  // Świadome „pokaż też oferty bez informacji o opłatach" — w adresie, żeby dało się je
  // udostępnić i żeby „wstecz" je odtworzyło.
  if (params.get('nieznane') === '1') filters.nieznaneOplaty = true;
  if (params.get('bezZdjec') === '0') filters.bezZdjec = false;
  if (params.get('bezAdresu') === '1') filters.bezAdresu = true;
  if (params.get('odzaraz') === '1') filters.availableNow = true;
  if (params.get('wlasciciel') === '1') filters.wlascicielNaFlatHero = true;
  if (params.get('ulubione') === '1') filters.ulubione = true;
  if (params.get('includeDead') === '1') filters.includeDead = true;
  if (params.get('noPrice') === '1') filters.noPrice = true;
  if (params.get('approxLoc') === '1') filters.approxLocation = true;
  if (params.get('sanityOutliers') === '1') filters.sanityOutliers = true;
  const sort = params.get('sort');
  if (sort === 'oldest' || sort === 'cheapest' || sort === 'expensive') {
    filters.sort = sort;
  }
  if (params.get('view') === 'map') filters.view = 'map';
  const poly = params.get('poly');
  if (poly) {
    const verts = poly
      .split(';')
      .map((pair) => pair.split(',').map(Number))
      .filter((p) => p.length === 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]))
      .map(([lat, lon]) => [lat, lon] as [number, number]);
    if (verts.length >= 3) filters.polygon = verts;
  }
  return filters;
}

/**
 * „Moje wyszukiwanie" w STANIE WPISU HISTORII (`location.state`) — wraca przy „wstecz" z oferty
 * i przy odświeżeniu, a nie jedzie w linku. Zgłoszenie Piotra 29.09: chip żył w `useState`
 * i przepadał przy każdym powrocie z oferty, choć filtry z profilu wracały z adresu.
 */
export function zProfiluZeStanu(stan: unknown): boolean {
  return !!stan && typeof stan === 'object' && (stan as { zProfilu?: unknown }).zProfilu === true;
}

/**
 * ŚWIADOME WEJŚCIE na wyszukiwarkę („Szukaj Mieszkania” w nagłówku): znacznik w stanie wpisu
 * historii, nie w adresie (nie jedzie w udostępnionym linku). Takie wejście wczytuje „Moje
 * wyszukiwanie” nawet wtedy, gdy człowiek zdjął je wcześniej w tej sesji („Wyczyść”), bo
 * kliknięcie wyszukiwarki z profilu jest świeższą decyzją niż tamto zdjęcie (zgłoszenie Maćka 1.10).
 */
export const ZAMIAR_MOJEGO_WYSZUKIWANIA = { wczytajMojeWyszukiwanie: true } as const;

export function zamiarMojegoWyszukiwania(stan: unknown): boolean {
  return !!stan && typeof stan === 'object' && (stan as { wczytajMojeWyszukiwanie?: unknown }).wczytajMojeWyszukiwanie === true;
}

/**
 * „ZMIEŃ W MOICH FILTRACH” (profil, „Czego szukam”, plan E6.3): wejście, które od razu otwiera
 * panel filtrów. Też w stanie wpisu, nie w adresie, i też jednorazowe — zdejmuje go `bezZamiaru`,
 * więc odświeżenie albo „wstecz” nie otwiera panelu drugi raz.
 */
export const ZAMIAR_PANELU_FILTROW = { otworzPanelFiltrow: true } as const;

export function zamiarPaneluFiltrow(stan: unknown): boolean {
  return !!stan && typeof stan === 'object' && (stan as { otworzPanelFiltrow?: unknown }).otworzPanelFiltrow === true;
}

/**
 * EDYCJA MOICH FILTRÓW Z PROFILU (Maciek 20.10): „Zmień w Moich filtrach” otwiera panel w trybie
 * edycji — tytuł „Edytuj Moje filtry”, bez pastylki i bez pola „Zapisz…”, w stopce „Anuluj” /
 * „Zapisz”, a zapis albo wyjście wraca do profilu. Jednorazowy, jak oba znaczniki wyżej.
 */
export const ZAMIAR_EDYCJI_Z_PROFILU = { edytujMojeFiltryZProfilu: true } as const;

export function zamiarEdycjiZProfilu(stan: unknown): boolean {
  return !!stan && typeof stan === 'object' && (stan as { edytujMojeFiltryZProfilu?: unknown }).edytujMojeFiltryZProfilu === true;
}

/** Stan wpisu bez znaczników jednorazowych wejść: zużywamy je raz, nie wracają przy odświeżeniu. */
export function bezZamiaru(stan: unknown): unknown {
  if (!zamiarMojegoWyszukiwania(stan) && !zamiarPaneluFiltrow(stan) && !zamiarEdycjiZProfilu(stan)) return stan;
  const {
    wczytajMojeWyszukiwanie: _pominiety, otworzPanelFiltrow: _panel, edytujMojeFiltryZProfilu: _edycja, ...reszta
  } = stan as Record<string, unknown>;
  return reszta;
}

/** Nowy stan wpisu: zachowuje cudze pola, podmienia tylko `zProfilu`. */
export function stanWpisuZProfilem(stan: unknown, zProfilu: boolean): Record<string, unknown> {
  const reszta = stan && typeof stan === 'object' ? (stan as Record<string, unknown>) : {};
  return { ...reszta, zProfilu };
}

/** Czy wpis historii trzeba przepisać: inny adres ALBO inny stan chipa. */
export function wpisDoPrzepisania(
  nowyAdres: string,
  biezacyAdres: string,
  stan: unknown,
  zProfilu: boolean,
): boolean {
  return nowyAdres !== biezacyAdres || zProfiluZeStanu(stan) !== zProfilu;
}
